import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { isUsUser } from '@/core/auth/token-claims'
import { BackButton } from '@/shared/components/back-button'
import { appConfig } from '@/shared/config/app-config'
import { ContactSupportFooter } from './contact-support-footer'

interface SupportFallbackPageProps {
  title: string
  // Optional secondary line under the title (e.g. an error detail + a support
  // email mailto link). Rendered muted grey.
  subtitle?: ReactNode
  showBack?: boolean
  onBack?: () => void
  onGoBackToProfile?: () => void
  onContactSupport: () => void
}

// Matches CancelFlowLayout transition so all redesigned screens share the
// same enter feel (subtle fade + rise, header stays static).
const TRANSITION_EASE = [0.16, 1, 0.3, 1] as const
const TRANSITION_DURATION = 0.4

// Shared layout shell for "contact support" fallback screens (pause / update
// payment / renew subscription). Brand-mark header → centered Playfair display
// title → footer with optional primary "Go back to profile" + Contact support.
export function SupportFallbackPage({
  title,
  subtitle,
  showBack = false,
  onBack,
  onGoBackToProfile,
  onContactSupport,
}: SupportFallbackPageProps) {
  const { t } = useTranslation()
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-white">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
        <header className="mb-7 flex shrink-0 items-center px-5 pt-[30px]">
          {showBack && onBack ? (
            <BackButton onClick={onBack} />
          ) : (
            <div className="size-9" />
          )}
          <img
            src={appConfig.assets.logoFull}
            alt={appConfig.name}
            className="mx-auto h-auto w-[120px]"
          />
          <div className="size-9" />
        </header>

        <motion.main
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: TRANSITION_DURATION, ease: TRANSITION_EASE }}
          className="flex flex-1 flex-col items-center px-5 pb-6"
        >
          <h1 className="text-center font-display text-[32px] font-medium leading-[40px] text-black">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 max-w-sm text-center font-inter text-[14px] leading-[18px] tracking-[-0.28px] text-app-grey-600">
              {subtitle}
            </p>
          )}
        </motion.main>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: TRANSITION_DURATION,
            delay: 0.08,
            ease: TRANSITION_EASE,
          }}
        >
          <ContactSupportFooter
            onContactSupport={onContactSupport}
            contactSupportLabel={t(
              'unsubscription_membershipInfo_contactSupport_web',
            )}
            primaryAction={
              onGoBackToProfile
                ? {
                    label: t(
                      isUsUser()
                        ? 'unsubscription_support_goBackToMembershipUs_web'
                        : 'unsubscription_support_goBackToMembership_web',
                    ),
                    onClick: onGoBackToProfile,
                  }
                : undefined
            }
          />
        </motion.div>
      </div>
    </div>
  )
}
